/**
 * EInvoiceService — thin HTTP client for the backend e-invoice endpoints.
 *
 * Backend contract (see backend-api/src/einvoice/einvoice.controller.ts):
 *   POST /api/einvoice/generate/:invoiceId  → { success, message, enqueued }
 *   GET  /api/einvoice/status/:invoiceId    → { status, irn, ackNo, ackDate, errorMessage, signedQr }
 *
 * Generation is async (BullMQ) so the UI must poll `getStatus` until
 * status is `GENERATED` or `FAILED`. Mobile uses a 2-second poll with
 * 10 attempts and we mirror that here.
 */

import { API_URL } from '@/config';
import { useAppStore } from '@/store/useAppStore';

export type EInvoiceStatus = 'NOT_GENERATED' | 'PENDING' | 'GENERATED' | 'FAILED';

export interface EInvoiceStatusResponse {
  status: EInvoiceStatus;
  isB2B?: boolean;
  irn?: string | null;
  ackNo?: string | null;
  ackDate?: string | null;
  errorMessage?: string | null;
  signedQr?: string | null;
}

export interface GenerateEInvoiceResponse {
  success: boolean;
  message?: string;
  enqueued?: boolean;
}

function authHeaders(): Record<string, string> {
  const token = useAppStore.getState().authToken;
  if (!token) throw new Error('Not authenticated.');
  return { Authorization: `Bearer ${token}` };
}

export async function triggerGeneration(invoiceId: string): Promise<GenerateEInvoiceResponse> {
  const res = await fetch(`${API_URL}/einvoice/generate/${invoiceId}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data?.message || `E-Invoice request failed (HTTP ${res.status}).`);
  }
  return data as GenerateEInvoiceResponse;
}

export async function getStatus(invoiceId: string): Promise<EInvoiceStatusResponse> {
  const res = await fetch(`${API_URL}/einvoice/status/${invoiceId}`, {
    method: 'GET',
    headers: authHeaders(),
  });
  if (!res.ok) {
    throw new Error(`E-Invoice status request failed (HTTP ${res.status}).`);
  }
  return (await res.json()) as EInvoiceStatusResponse;
}
